import { useContext, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation } from 'react-router';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { toast } from 'react-toastify';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import { patientInfoSchema } from 'utils/schema';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  addPatientDetails,
  getPatientDetails,
  getPatientDetailsFromAdmin,
  updatePatientDetails,
  getCityState,
  getSiblingAutoComplete,
} from 'store/slices/userSlice';
import { AuthContext } from 'utils/AuthContext';
import { Restricted } from 'components/Restricted';
import { BiologicalGender, CustodyType, Locations, permissions, Pronouns, YES } from 'utils/dropDownList';
import { StyledDialog } from 'theme/index';

const PatientInfo = (props: any) => {
  const [dobDisabled, setDobDisabled] = useState(false);
  const [agreementTooltipOpen, setAgreementTooltipOpen] = useState(false);
  const [preferredNameTooltip, setPreferredNameTooltip] = useState(false);
  const [disableField, setDisableField] = useState(false);
  const [preferredGenderDisable, setPreferredGenderDisable] = useState(false);
  const [biologicalGenderDisable, setBiologicalGenderDisable] = useState(false);
  const [preferredNameDisable, setPreferredNameDisable] = useState(false);
  const [formData, setFormData] = useState<any>();

  const [displayInsuranceMessage, setDisplayInsuranceMessage] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [openPreferedLocationContnet, setOpenPreferedLocationContnet] = useState<boolean>(false);
  const [preferredLocation, setPreferredLocation] = useState<string>();
  const [readOnlyWarningDialog, setReadOnlyWarningDialog] = useState<boolean>(false);
  const [olderThan18, setOlderThan18] = useState<boolean>(false);
  const [is12ThGrade, setIs12ThGrade] = useState<boolean>(false);
  const [dateError, setDateError] = useState<boolean>(false);
  const [agreementMessage, setAgreementMessage] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    getValues,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(patientInfoSchema),
    mode: 'all',
  });

  const dispatch = useAppDispatch();
  const isProfileDetailsFetched = useAppSelector((state) => state.user.loaders.isProfileDetailsFetched);
  const { state } = useLocation();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { user, setUserDetails } = useContext(AuthContext);
  const addSiblings = query.get('addSiblings') === 'true';
  const isAdmin = user.isAdmin;
  const patientId = isAdmin ? props?.patientId || state.patientId : user?.patientId;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(tooltipRef.current && !tooltipRef.current.contains(event.target as Node))) {
        return;
      }
      setOpenPreferedLocationContnet(false);
      setAgreementTooltipOpen(false);
      setPreferredNameTooltip(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    //eslint-disable-next-line
  }, []);

  const preferedLocationContent = (
    <Typography>
      Storing your nearest MindWeal location helps us personalize your care, offering you the most relevant services and
      updates from the office that is closest to you. You can update your preferred office location anytime through this
      section. It will apply to future appointments, while any existing bookings will remain at the previously selected
      location
    </Typography>
  );

  const preferredNameTooltipContent = <Typography>Fill only if different from first name</Typography>;
  //tooltip content
  const agreementTooltipContent = (
    <Typography variant='body1'>
      Please email a copy of the agreement to{' '}
      <Link href='mailto:newpatient@mindweal.com' variant='body2'>
        newpatient@mindweal.com
      </Link>
      , including the patient&apos;s name and any relevant details. Use &apos;Parental Agreement - [Patient&apos;s
      Name]&apos; as the subject line.
    </Typography>
  );

  const getPatient = async () => {
    if (addSiblings) {
      return;
    }
    await dispatch(getPatientDetails(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          Object.keys(response?.data).forEach((key: any) => {
            if (key === 'isActiveCustody') {
              const custody = response?.data.isActiveCustody ? 'yes' : 'no';
              setValue(key, custody, { shouldValidate: true, shouldDirty: false });
              setAgreementMessage(response?.data.isActiveCustody);
            } else if (response.data[key] === null) setValue(key, '', { shouldValidate: false });
            else setValue(key, response.data[key], { shouldValidate: false });
          });
          if (response?.data?.invalidInsurance) {
            setDisplayInsuranceMessage(true);
          }
          setGuardianPermission(response?.data?.firstName, response?.data?.id);
        }
      });
  };
  const getPatientFromAdmin = async () => {
    await dispatch(getPatientDetailsFromAdmin(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setAdminUserPermission();
          Object.keys(response?.data).forEach((key: any) => {
            if (key === 'isActiveCustody') {
              const custody = response?.data.isActiveCustody ? 'yes' : 'no';
              setValue(key, custody, { shouldValidate: true, shouldDirty: false });
              setAgreementMessage(response?.data.isActiveCustody);
            } else if (response.data[key] === null) setValue(key, '', { shouldValidate: false });
            else setValue(key, response.data[key], { shouldValidate: false });
          });
        }
      });
  };
  const setGuardianPermission = (firstName: string, id: string) => {
    if (!isAdmin && user?.permissions?.includes('EDIT_PROFILE') && firstName) {
      setPreferredGenderDisable(false);
      setPreferredNameDisable(false);
      setBiologicalGenderDisable(true);
      setDisableField(true);
      if (id !== undefined) {
        setDobDisabled(true);
      }
    }
  };

  const setAdminUserPermission = () => {
    if (!isAdmin && !user?.permissions?.includes('GLOBAL_EDIT_PROFILE')) {
      setDisableField(true);
      setPreferredGenderDisable(true);
      setPreferredNameDisable(true);
      setBiologicalGenderDisable(true);
      setDobDisabled(true);
    }
  };

  useEffect(() => {
    setValue('dateOfBirth', dayjs(user?.childBirthDate).format('MM/DD/YYYY'));
    if (isAdmin) getPatientFromAdmin();
    else if (!isAdmin && addSiblings) {
      dispatch(getSiblingAutoComplete({}))
        .unwrap()
        .then((res) => {
          if (res.status === 200) {
            setValue('childAddress', res?.data?.childAddress ? res?.data?.childAddress : '');
            setValue('city', res?.data?.city ? res?.data?.city : '');
            setValue('state', res?.data?.state ? res?.data?.state : '');
            setValue('zipcode', res?.data?.zipcode ? res?.data?.zipcode : '');
            setValue('preferredLocation', res?.data?.preferredLocation ? res?.data?.preferredLocation : '');
            setPreferredLocation(res?.data?.preferredLocation ? res?.data?.preferredLocation : '');
          }
        });
      setValue('biologicalGender', '');
      setValue('dateOfBirth', '');
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('preferredGender', '');
      setValue('preferredName', '');

      setBiologicalGenderDisable(false);
      setDisableField(false);
      setPreferredNameDisable(false);
      setPreferredGenderDisable(false);
      setDobDisabled(false);
    } else getPatient();
    //eslint-disable-next-line
  }, [props]);

  const getPayload = (formData: any) => {
    const {
      id,
      firstName,
      lastName,
      biologicalGender,
      preferredGender,
      preferredName,
      childAddress,
      zipcode,
      state,
      city,
      preferredLocation,
      isActiveCustody,
    } = formData;

    let resolvedId = patientId;
    if (addSiblings) {
      resolvedId = null;
    } else if (id) {
      resolvedId = id;
    }

    return {
      id: resolvedId,
      siblingFlow: addSiblings ? 'addSiblings' : null,
      firstName,
      lastName,
      dateOfBirth: getValues('dateOfBirth'),
      biologicalGender,
      preferredGender,
      preferredName,
      childAddress,
      zipcode,
      state,
      city,
      preferredLocation,
      isActiveCustody: isActiveCustody === YES,
    };
  };
  //handle PG ActiveParenting (default value NO)
  const handlePGActiveParenting = (e: any) => {
    if (e.target.value === YES) {
      setAgreementMessage(true);
    } else {
      setAgreementMessage(false);
    }
    setValue('isActiveCustody', e.target.value, { shouldValidate: true });
  };

  const savePatient = async (e: any) => {
    if (dateError || formData?.dateOfBirth === '') {
      setDateError(true);
      return;
    }
    if (isValid && isDirty) {
      addPatient(formData);
      setReadOnlyWarningDialog(false);
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  const validateDateOfBirthIfMoreThan18 = async (e: any) => {
    if (formData?.dateOfBirth === '') {
      setDateError(true);
      return;
    }
    setIs12ThGrade(false);
    setOlderThan18(false);
    setDateError(false);
  };

  const closeIfGradeNotProvided = async () => {
    setIs12ThGrade(true);
    setOlderThan18(false);
    setDateError(true);
  };

  const addPatient = (formData: any) => {
    const payload = {
      ...getPayload(formData),
    };

    if (addSiblings) {
      delete payload.id;
    }

    dispatch(addPatientDetails(payload))
      .unwrap()
      .then((response: any) => {
        if (response.status === 201) {
          const message = 'Patient details added successfully.';
          toast(message, {
            type: 'success',
            icon: true,
          });
          reset(formData);
        }
        const userData = {
          ...user,
          patientId: response?.data?.id,
          zipcode: payload?.zipcode,
          addedNewPatient: true,
        };
        setValue('id', response?.data?.id, { shouldValidate: true });
        setUserDetails(userData);
        props?.onComplete();
      });
  };

  const updatePatient = (formData: any) => {
    const payload = getPayload(formData);
    dispatch(updatePatientDetails(payload))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          const message = 'Patient details updated successfully.';
          toast(message, {
            type: 'success',
            icon: true,
          });
          reset(formData);
          const userData = {
            ...user,
            zipcode: payload?.zipcode,
          };
          setUserDetails(userData);
          if (response?.data?.invalidInsurance) {
            setDisplayInsuranceMessage(true);
            setTimeout(() => {
              props?.onComplete();
            }, 10000);
          } else {
            props?.onComplete();
          }
        }
      });
  };

  const onSubmitForm = (formData: any) => {
    if (dateError || formData?.dateOfBirth === '') {
      setDateError(true);
      return;
    }

    if (isValid && isDirty) {
      setFormData(formData);

      // sibling flow should always ADD
      if (addSiblings) {
        setReadOnlyWarningDialog(true);
        return;
      }

      // normal existing patient flow
      if (formData?.id) {
        formData.dateOfBirth = getValues('dateOfBirth');
        updatePatient(formData);
      } else {
        setReadOnlyWarningDialog(true);
      }
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  const handleZipcodeChange = (e: any) => {
    if (e.target.value.length === 5) {
      dispatch(getCityState(e.target.value))
        .unwrap()
        .then((res: any) => {
          if (res.status === 200) {
            setValue('city', res?.data?.primaryCity, { shouldDirty: true });
            setValue('state', res?.data?.state, { shouldDirty: true });
            setValue('zipcode', e.target.value, { shouldDirty: true, shouldValidate: true });
          }
        })
        .catch((e: any) => {
          if (e.err.errorCode === 3006) {
            toast('No service available in your area', {
              type: 'error',
              icon: true,
            });
            setValue('zipcode', '', { shouldDirty: false });
            setValue('city', '', { shouldDirty: false });
            setValue('state', '', { shouldDirty: false });
          }
        });
    }
  };

  const handlePreferredLocation = (e: any) => {
    setValue('preferredLocation', e.target.value, { shouldDirty: true, shouldValidate: true });
    setPreferredLocation(e.target.value);
  };

  const handleDateChange = (val: any) => {
    const data = dayjs(val).format('YYYY-MM-DD');
    const age = dayjs().diff(dayjs(data), 'day') / 365.25;
    if (new Date(val).getFullYear() > 1000) {
      setIs12ThGrade(false);
      setOlderThan18(false);
      if (Number(age) >= 3) {
        setValue('dateOfBirth', dayjs(val).format('MM/DD/YYYY'), {
          shouldValidate: false,
          shouldDirty: true,
        });
        register('dateOfBirth');
        if (Number(age) < 18) {
          setDateError(false);
        } else {
          setOlderThan18(true);
          setIs12ThGrade(true);
        }
      } else {
        setDateError(true);
      }
    }
  };

  return (
    <Container component='main' maxWidth='sm' key={props?.newSibling}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box component='form' noValidate sx={{ mt: 1 }} onSubmit={handleSubmit((formData) => onSubmitForm(formData))}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} />
            <Grid item xs={12} sm={12}>
              <Typography
                variant='body1'
                gutterBottom
                sx={{
                  fontSize: '14px',
                  lineHeight: 1,
                  bgcolor: 'rgb(238, 242, 255)',
                  p: '12px',
                  borderRadius: 'information.main',
                  border: '1px solid #EAECF0',
                }}
              >
                <b>IMPORTANT: </b>
                Please enter the patient’s information here — not the guardian’s.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} />
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='firstName'>Patient’s First Name*</InputLabel>

              <TextField
                autoComplete='first-name'
                placeholder='Enter Patient’s First Name'
                required
                fullWidth
                id='firstName'
                {...register('firstName')}
                error={!!errors.firstName}
                helperText={errors?.firstName?.message}
                inputProps={{
                  maxLength: 255,
                }}
                disabled={disableField}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='lastName'>{isAdmin ? 'Patient’s Last Name' : 'Patient’s Last Name*'}</InputLabel>
              <TextField
                required
                fullWidth
                placeholder='Enter Patient’s Last Name'
                id='lastName'
                autoComplete='last-name'
                {...register('lastName')}
                error={!!errors.lastName}
                helperText={errors?.lastName?.message}
                disabled={disableField}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='preferredName' sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                Preferred Name{' '}
                <Tooltip
                  title={preferredNameTooltipContent}
                  arrow
                  enterTouchDelay={0} // ✅ show immediately
                  leaveTouchDelay={3000} // ✅ stay visible longer
                >
                  <IconButton size='small' sx={{ ml: 0.5 }}>
                    <InfoOutlinedIcon fontSize='small' color='info' />
                  </IconButton>
                </Tooltip>
              </InputLabel>

              <TextField
                fullWidth
                placeholder='Enter Patient’s Preferred Name'
                id='preferredName'
                autoComplete='preferredName'
                {...register('preferredName')}
                inputProps={{
                  maxLength: 255,
                }}
                disabled={preferredNameDisable}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='dateOfBirth'>{isAdmin ? 'Date of Birth' : 'Date of Birth*'}</InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={dobDisabled}
                  sx={{ width: '100%' }}
                  disableFuture
                  onChange={(e) => handleDateChange(e)}
                  value={getValues('dateOfBirth') ? dayjs(getValues('dateOfBirth')) : null}
                />
              </LocalizationProvider>
              {dateError && !is12ThGrade ? (
                <Typography color='#d32f2f' sx={{ fontSize: '12px' }}>
                  Patient&apos;s age must be greater than 4{' '}
                </Typography>
              ) : null}
              {is12ThGrade ? (
                <Typography color='#d32f2f' sx={{ fontSize: '12px' }}>
                  Please select valid date{' '}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='biologicalGender'>{isAdmin ? 'Biological Gender' : 'Biological Gender*'}</InputLabel>
              <TextField
                id='biologicalGender'
                select // tell TextField to render select
                fullWidth
                {...register('biologicalGender')}
                error={!!errors.biologicalGender}
                helperText={errors?.biologicalGender?.message}
                value={watch('biologicalGender') ? watch('biologicalGender') : ''}
                disabled={biologicalGenderDisable}
              >
                {BiologicalGender.map((gender) => (
                  <MenuItem key={gender.key} value={gender.value} disabled={biologicalGenderDisable}>
                    {gender.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='preferredGender'>Preferred Pronoun</InputLabel>

              <TextField
                id='preferredGender'
                select // tell TextField to render select
                fullWidth
                {...register('preferredGender')}
                value={watch('preferredGender') ? watch('preferredGender') : 'choose_preferred_gender'}
                disabled={preferredGenderDisable}
              >
                {Pronouns.map((p) => (
                  <MenuItem key={p.key} value={p.value}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='childaddress'>Street Address*</InputLabel>
              <TextField
                fullWidth
                multiline
                placeholder='Enter patient&#39;s current address'
                id='childaddress'
                autoComplete='dchildaddressob'
                variant='filled'
                {...register('childAddress')}
                error={!!errors.childAddress}
                helperText={errors?.childAddress?.message}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='zipcode'>ZIP Code*</InputLabel>
              <TextField
                required
                fullWidth
                placeholder='Enter ZIP Code'
                id='zipcode'
                autoComplete='zipcode'
                {...register('zipcode')}
                error={!!errors.zipcode}
                helperText={errors?.zipcode?.message}
                inputProps={{
                  maxLength: 5,
                }}
                onChange={(e: any) => handleZipcodeChange(e)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='city'>City</InputLabel>
              <TextField
                fullWidth
                placeholder='City'
                id='city'
                autoComplete='city'
                disabled
                {...register('city')}
                error={!!errors.city}
                helperText={errors?.city?.message}
                value={watch('city') ? watch('city') : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='state'>{'State'}</InputLabel>
              <TextField
                required
                fullWidth
                placeholder='State'
                id='state'
                autoComplete='state'
                disabled
                {...register('state')}
                error={!!errors.state}
                helperText={errors?.state?.message}
                inputProps={{
                  maxLength: 255,
                }}
                value={watch('state') ? watch('state') : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel htmlFor='preferredLocation'>
                Preferred Clinic Location*{' '}
                <Tooltip
                  title={preferedLocationContent}
                  arrow
                  enterTouchDelay={0} // ✅ show immediately
                  leaveTouchDelay={3000} // ✅ stay visible longer
                >
                  <IconButton size='small' sx={{ ml: 0.5 }}>
                    <InfoOutlinedIcon fontSize='small' color='info' />
                  </IconButton>
                </Tooltip>
              </InputLabel>

              <TextField
                select
                required
                fullWidth
                id='preferredLocation'
                {...register('preferredLocation')}
                error={!!errors.preferredLocation}
                helperText={errors?.preferredLocation?.message}
                value={watch('preferredLocation') || ''}
                onChange={handlePreferredLocation}
                SelectProps={{
                  renderValue: (selected) => {
                    const value = selected as string; // 👈 fix

                    return (
                      <div
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: '1.5',
                        }}
                      >
                        {value}
                      </div>
                    );
                  },
                }}
                sx={{
                  // 🔥 Root input container (VERY IMPORTANT)
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start', // 👈 allow top alignment
                    height: 'auto', // 👈 allow full growth
                  },

                  // 🔥 Actual selected value container
                  '& .MuiSelect-select': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: '1.5',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    height: 'auto !important', // 👈 override MUI default
                    display: 'block', // 👈 remove flex restriction
                  },

                  // 🔥 Remove default min height restriction
                  '& .MuiOutlinedInput-input': {
                    height: 'auto !important',
                  },
                }}
              >
                <MenuItem value='' disabled>
                  Choose Location
                </MenuItem>

                {Locations.map((l) => (
                  <MenuItem
                    key={l.id}
                    value={l.location}
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {l.location}
                  </MenuItem>
                ))}
              </TextField>
              {preferredLocation &&
                // eslint-disable-next-line array-callback-return
                Locations.map((l: any) => {
                  if (preferredLocation === l.location) {
                    return (
                      <Grid
                        container
                        key={l.id}
                        sx={{
                          mt: '12px',
                          p: '12px',
                          border: '1px solid #EAECF0',
                          borderRadius: '8px',
                          background: '#F8F5FF',
                        }}
                      >
                        <Grid item xs={0.5}>
                          <CardMedia
                            component={'img'}
                            src='../images/location.png'
                            sx={{ height: '17px', width: '14px' }}
                          />
                        </Grid>
                        <Grid item xs={11.5} paddingLeft={'5px'}>
                          {' '}
                          {l.instruction}
                        </Grid>
                      </Grid>
                    );
                  }
                })}
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <InputLabel htmlFor='custody' sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                  <b>Is there any active custody or parenting agreement regarding the patient </b>{' '}
                  <Tooltip
                    title={agreementTooltipContent}
                    arrow
                    enterTouchDelay={0} // ✅ show immediately
                    leaveTouchDelay={3000} // ✅ stay visible longer
                  >
                    <IconButton size='small' sx={{ ml: 0.5 }}>
                      <InfoOutlinedIcon fontSize='small' color='info' />
                    </IconButton>
                  </Tooltip>
                </InputLabel>
                <FormControl error={!!errors.isActiveCustody}>
                  <RadioGroup
                    row
                    onChange={handlePGActiveParenting}
                    name='PGactiveParenting'
                    value={watch('isActiveCustody') ? watch('isActiveCustody') : null}
                  >
                    {CustodyType.map((custody) => (
                      <FormControlLabel
                        key={custody.key}
                        value={custody.key}
                        control={<Radio />}
                        label={custody.label}
                        {...register('isActiveCustody')}
                        disabled={disableField}
                      />
                    ))}
                  </RadioGroup>
                  {agreementMessage ? (
                    <Alert severity='info' sx={{ fontSize: '13px', lineHeight: 1 }}>
                      Please email a copy of the agreement to newpatient@mindweal.com, including the patient&apos;s name
                      and any relevant details. Use &lsquo;Parental Agreement - [Patient&apos;s Name]&lsquo; as the
                      subject line.
                    </Alert>
                  ) : null}
                  <FormHelperText>
                    {errors.isActiveCustody ? errors.isActiveCustody?.message?.toString() : ''}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Restricted permission={permissions.EDIT_PROFILE}>
              <Grid container justifyContent='center'>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <LoadingButton
                    loading={isProfileDetailsFetched}
                    variant='contained'
                    type='submit'
                    sx={{
                      minWidth: 140,
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    Save
                  </LoadingButton>
                </Grid>
              </Grid>
            </Restricted>
          </Grid>
        </Box>
      </Box>
      <StyledDialog
        open={readOnlyWarningDialog}
        maxWidth='xs' // ✅ smaller dialog
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {' '}
          {/* ✅ reduce padding */}
          <Grid container spacing={1.5} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Confirm Patient Information
              </Typography>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', px: 1 }}>
                Please review before saving. This information cannot be edited later.
              </Typography>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1, // ✅ smaller gap
                  justifyContent: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  mt: 1, // ✅ reduce spacing
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 90, // ✅ smaller button
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                    textTransform: 'none',
                  }}
                  onClick={(e) => savePatient(e)}
                >
                  Save
                </Button>

                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 90,
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                    textTransform: 'none',
                  }}
                  onClick={() => setReadOnlyWarningDialog(false)}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
      <StyledDialog open={olderThan18}>
        <DialogContent>
          <Grid container spacing={2} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Is your child in grade 12 or below?
              </Typography>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography
                variant='body2'
                sx={{
                  maxWidth: 600,
                  mx: 'auto', // ✅ center content
                  px: { xs: 1, sm: 3 }, // ✅ responsive padding
                }}
              >
                At MindWeal, we specialize in caring for children between the ages of 4 and 18. We also offer our
                expertise to individuals over 18 who are still in high school.
              </Typography>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexDirection: { xs: 'column', sm: 'row' }, // ✅ mobile fix
                  mt: 2,
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  fullWidth
                  sx={{
                    background: '#F04438',
                    '&:hover': { background: '#f02719' },
                    maxWidth: { sm: 160 },
                    textTransform: 'none',
                  }}
                  onClick={(e) => validateDateOfBirthIfMoreThan18(e)}
                >
                  Yes
                </Button>

                <Button
                  variant='contained'
                  size='small'
                  fullWidth
                  sx={{
                    maxWidth: { sm: 160 },
                    textTransform: 'none',
                  }}
                  onClick={() => closeIfGradeNotProvided()}
                >
                  No
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default PatientInfo;
